import { motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { Link } from "react-router";

import type { ChatDto } from "@/api/types";
import { ChatPreview } from "@/components/ChatPreview/ChatPreview";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { LogOut01Icon } from "@/components/Icons/LogOut01Icon";
import { Pin01Icon } from "@/components/Icons/Pin01Icon";
import { PinFilled01Icon } from "@/components/Icons/PinFilled01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { useSlug } from "@/hooks/useSlug";
import { chatMutations } from "@/queries/chats/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface ChatListItemProps {
  chat: ChatDto;
  canPinMoreChat: boolean;
  onPinLimitExceeded: () => void;
}

export function ChatListItem({ chat, canPinMoreChat, onPinLimitExceeded }: ChatListItemProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const mutationDeleteChat = chatMutations.useDeleteChat();
  const mutationLeaveGroupChat = chatMutations.useLeaveGroupChat();
  const mutationPinChat = chatMutations.usePinChat();
  const mutationUnpinChat = chatMutations.useUnpinChat();
  const postHog = usePostHog();

  const getChatPath = () => {
    if (chat.chatType === "single") {
      return routes.users.details({
        slug,
        id: chat.privateChatInfo!.receiver.id,
      });
    } else {
      return routes.chats.details({
        slug,
        id: chat.id,
      });
    }
  };

  const isChatGroupChat = chat.chatType === "group";
  const isLeaveActionAvailable = isChatGroupChat && chat.groupChatInfo?.isMember && chat.groupChatInfo?.canLeave;
  const isDeleteActionAvailable = !isChatGroupChat || !chat.groupChatInfo?.isMember;

  const handlePinChat = () => {
    if (!canPinMoreChat) {
      postHog.capture("pin_chat_exceeded");
      onPinLimitExceeded();

      return;
    }
    postHog.capture("clicked_pin_chat");
    mutationPinChat.mutate(chat.id);
  };

  const handleUnpinChat = () => {
    postHog.capture("clicked_unpin_chat");
    mutationUnpinChat.mutate(chat.id);
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("component.chat-list-item.context-menu.pin-chat"),
      },
      value: "pin-chat",
      "data-testid": "context-menu-pin-chat-btn",
      icon: <PinFilled01Icon />,
      callbackFn: handlePinChat,
      isHidden: Boolean(chat.pinnedAt),
    },
    {
      labels: {
        default: t("component.chat-list-item.context-menu.unpin-chat"),
      },
      value: "unpin-chat",
      "data-testid": "context-menu-unpin-chat-btn",
      icon: <Pin01Icon />,
      callbackFn: handleUnpinChat,
      isHidden: !chat.pinnedAt,
    },
    {
      labels: {
        default: t("common.action.leave"),
      },
      value: "leave",
      "data-testid": "context-menu-leave-btn",
      icon: <LogOut01Icon />,
      callbackFn: () => mutationLeaveGroupChat.mutate(chat.id),
      isHidden: !isLeaveActionAvailable,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      "data-testid": "context-menu-delete-btn",
      icon: <Trash02Icon />,
      callbackFn: () => mutationDeleteChat.mutate(chat.id),
      isHidden: !isDeleteActionAvailable,
    },
  ];

  return (
    <motion.li
      className="flex w-full items-center overflow-hidden border-b border-b-border-default bg-background-default transition-colors duration-100 last:border-b-0 hover:bg-background-secondary"
      data-testid="chat-list-item"
      transition={{
        type: "tween",
        ease: "easeInOut",
        duration: 0.2,
      }}
      layout
    >
      <Link className="min-w-0 flex-1 p-space-md hocus:outline-none" to={getChatPath()}>
        <ChatPreview chat={chat} showLatestReplyDate showUnreadRepliesCount showStatus />
      </Link>
      <aside className="pr-space-sm">
        <ContextMenu items={menuActions} />
      </aside>
    </motion.li>
  );
}
